/*
  Pixelpa — Photoshop-style Preferences modal with a left-rail of 17
  categories. Each category renders a small panel of real settings; the
  rest of the surface area is "Coming soon" placeholder text so the user
  sees the full Photoshop information architecture rather than the prior
  4-item flat dialog. Closes #148.

  Persistence: settings live under localStorage key "pixelpa.prefs".
  load() / save() / defaults() are exported pure for tests and operate
  on a plain Preferences object so the same persistence works without
  a DOM (e.g. in vitest jsdom).

  Open the dialog with `await new PreferencesDialog().open(currentPrefs)`.
  Resolves to the updated Preferences on OK, or `null` on Cancel.

  Side-effect on OK: applies theme by toggling `pp-theme-light` /
  `pp-theme-dark` class on document.body so any other DOM scope can
  react without coupling to this module.
*/

// ---------- Pure types & persistence ----------

export type ThemeChoice = "dark" | "light" | "auto";
export type CursorChoice = "precise" | "normal" | "both";
export type RulerUnit = "pixels" | "inches" | "cm" | "mm" | "points" | "picas" | "percent";

export interface Preferences {
  // General
  imageInterpolation: "nearest" | "bilinear" | "bicubic" | "bicubic-sharper" | "bicubic-smoother";
  resizeOnPaste: boolean;
  animatedZoom: boolean;
  // Interface
  theme: ThemeChoice;
  uiFontSize: "small" | "medium" | "large";
  uiLanguage: string;
  // Workspace
  openDocsAsTabs: boolean;
  enableFloatingDocs: boolean;
  // Tools
  showTooltips: boolean;
  zoomWithScrollWheel: boolean;
  shiftKeyForToolSwitch: boolean;
  // History Log
  historyStates: number;
  historyLogEnabled: boolean;
  // File Handling
  imagePreviews: "always" | "ask" | "never";
  appendPsdCompat: "always" | "ask" | "never";
  // Export
  quickExportFormat: "png" | "jpeg" | "webp";
  quickExportLocation: "ask" | "desktop" | "documents";
  // Performance
  memoryUsagePercent: number;
  cacheLevels: number;
  cacheTileSize: 128 | 256 | 512 | 1024;
  // Scratch Disks (read-only list in browser)
  scratchDiskNote: string;
  // Cursors
  paintingCursor: "standard" | "precise" | "normal-brush-tip" | "full-size-brush-tip";
  otherCursor: "standard" | "precise";
  cursorStyle: CursorChoice;
  // Transparency & Gamut
  gridSize: number;
  gridColor1: string;
  gridColor2: string;
  gamutWarningColor: string;
  // Units & Rulers
  rulerUnit: RulerUnit;
  typeUnit: "pixels" | "points" | "mm";
  // Guides, Grid & Slices
  guideColor: string;
  gridColor: string;
  gridSubdivisions: number;
  // Plug-Ins
  pluginAllowInternet: boolean;
  // Type
  smartQuotes: boolean;
  showFontNamesEnglish: boolean;
  // 3D
  threeDQuality: "draft" | "good" | "best";
  // Camera Raw
  cameraRawAutoTone: boolean;
}

export type Category =
  | "General"
  | "Interface"
  | "Workspace"
  | "Tools"
  | "History Log"
  | "File Handling"
  | "Export"
  | "Performance"
  | "Scratch Disks"
  | "Cursors"
  | "Transparency & Gamut"
  | "Units & Rulers"
  | "Guides, Grid & Slices"
  | "Plug-Ins"
  | "Type"
  | "3D"
  | "Camera Raw";

export const CATEGORIES: Category[] = [
  "General",
  "Interface",
  "Workspace",
  "Tools",
  "History Log",
  "File Handling",
  "Export",
  "Performance",
  "Scratch Disks",
  "Cursors",
  "Transparency & Gamut",
  "Units & Rulers",
  "Guides, Grid & Slices",
  "Plug-Ins",
  "Type",
  "3D",
  "Camera Raw",
];

export const STORAGE_KEY = "pixelpa.prefs";

export function defaults(): Preferences {
  return {
    imageInterpolation: "bicubic",
    resizeOnPaste: true,
    animatedZoom: true,
    theme: "dark",
    uiFontSize: "medium",
    uiLanguage: "en-US",
    openDocsAsTabs: true,
    enableFloatingDocs: true,
    showTooltips: true,
    zoomWithScrollWheel: true,
    shiftKeyForToolSwitch: true,
    historyStates: 50,
    historyLogEnabled: false,
    imagePreviews: "always",
    appendPsdCompat: "ask",
    quickExportFormat: "png",
    quickExportLocation: "ask",
    memoryUsagePercent: 70,
    cacheLevels: 4,
    cacheTileSize: 256,
    scratchDiskNote: "Browser sandbox — uses IndexedDB. Native scratch disks N/A.",
    paintingCursor: "normal-brush-tip",
    otherCursor: "standard",
    cursorStyle: "both",
    gridSize: 10,
    gridColor1: "#cccccc",
    gridColor2: "#999999",
    gamutWarningColor: "#7f7f7f",
    rulerUnit: "pixels",
    typeUnit: "points",
    guideColor: "#00aaff",
    gridColor: "#808080",
    gridSubdivisions: 4,
    pluginAllowInternet: false,
    smartQuotes: true,
    showFontNamesEnglish: false,
    threeDQuality: "good",
    cameraRawAutoTone: false,
  };
}

export function load(storage: Storage | null = typeof localStorage !== "undefined" ? localStorage : null): Preferences {
  if (!storage) return defaults();
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return defaults();
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    return { ...defaults(), ...parsed };
  } catch {
    return defaults();
  }
}

export function save(prefs: Preferences, storage: Storage | null = typeof localStorage !== "undefined" ? localStorage : null): void {
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* quota or denied — ignore */
  }
}

/**
 * Apply theme by toggling body classes. Pure-ish — only touches document.body
 * when a body exists. Tests in jsdom can call this safely.
 */
export function applyTheme(theme: ThemeChoice, doc: Document | null = typeof document !== "undefined" ? document : null): void {
  if (!doc?.body) return;
  doc.body.classList.remove("pp-theme-dark", "pp-theme-light", "pp-theme-auto");
  doc.body.classList.add(`pp-theme-${theme}`);
}

// ---------- DOM helpers ----------

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

function row(label: string, control: HTMLElement): HTMLDivElement {
  const r = el("div", "prefs__row");
  const l = el("label", "prefs__row-label");
  l.textContent = label;
  r.appendChild(l);
  r.appendChild(control);
  return r;
}

function mkSelect<T extends string>(value: T, options: Array<[T, string]>, onChange: (v: T) => void): HTMLSelectElement {
  const sel = el("select", "prefs__select");
  for (const [v, label] of options) {
    const opt = document.createElement("option");
    opt.value = v;
    opt.textContent = label;
    sel.appendChild(opt);
  }
  sel.value = value;
  sel.addEventListener("change", () => onChange(sel.value as T));
  return sel;
}

function mkCheckbox(checked: boolean, onChange: (v: boolean) => void): HTMLInputElement {
  const c = el("input");
  c.type = "checkbox";
  c.checked = checked;
  c.addEventListener("change", () => onChange(c.checked));
  return c;
}

function mkNumber(value: number, min: number, max: number, step: number, onChange: (v: number) => void): HTMLInputElement {
  const n = el("input");
  n.type = "number";
  n.min = String(min);
  n.max = String(max);
  n.step = String(step);
  n.value = String(value);
  n.addEventListener("change", () => {
    const v = parseFloat(n.value);
    if (Number.isFinite(v)) onChange(Math.max(min, Math.min(max, v)));
  });
  return n;
}

function mkColor(value: string, onChange: (v: string) => void): HTMLInputElement {
  const c = el("input");
  c.type = "color";
  c.value = value;
  c.addEventListener("change", () => onChange(c.value));
  return c;
}

function placeholderNote(text: string): HTMLDivElement {
  const d = el("div", "prefs__placeholder");
  d.textContent = text;
  return d;
}

// ---------- Component ----------

export class PreferencesDialog {
  private built = false;
  private root!: HTMLDivElement;
  private sheet!: HTMLDivElement;
  private rail!: HTMLDivElement;
  private body!: HTMLDivElement;
  private tabEls = new Map<Category, HTMLButtonElement>();
  private btnOK!: HTMLButtonElement;
  private btnCancel!: HTMLButtonElement;

  private prefs: Preferences = defaults();
  private activeCategory: Category = "General";

  private resolveFn: ((value: Preferences | null) => void) | null = null;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;
  private outsideHandler: ((e: MouseEvent) => void) | null = null;
  private openerEl: Element | null = null;

  open(initial?: Partial<Preferences>): Promise<Preferences | null> {
    if (!this.built) this.build();
    this.prefs = { ...load(), ...initial };
    if (this.resolveFn) {
      const prev = this.resolveFn;
      this.resolveFn = null;
      prev(null);
    }
    // Remember opener for focus restoration (#47).
    this.openerEl = document.activeElement;
    this.renderCategory(this.activeCategory);
    this.refreshTabs();
    this.root.style.display = "";
    return new Promise<Preferences | null>((resolve) => {
      this.resolveFn = resolve;
    });
  }

  close(): void {
    this.finish(null);
  }

  destroy(): void {
    if (this.keyHandler) window.removeEventListener("keydown", this.keyHandler, true);
    this.keyHandler = null;
    if (this.outsideHandler && this.built) this.root.removeEventListener("mousedown", this.outsideHandler);
    this.outsideHandler = null;
    if (this.built && this.root.parentNode) this.root.parentNode.removeChild(this.root);
    this.built = false;
  }

  private build(): void {
    this.root = el("div", "prefs");
    this.root.style.display = "none";

    // ARIA: modal dialog accessibility (#47)
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "true");
    this.root.setAttribute("aria-labelledby", "prefs-title");

    this.sheet = el("div", "prefs__sheet");
    this.root.appendChild(this.sheet);

    const title = el("div", "prefs__title");
    title.id = "prefs-title";
    title.textContent = "Preferences";
    this.sheet.appendChild(title);

    const main = el("div", "prefs__main");

    // Left rail of categories
    this.rail = el("div", "prefs__rail");
    for (const cat of CATEGORIES) {
      const btn = el("button", "prefs__tab");
      btn.type = "button";
      btn.textContent = cat;
      btn.dataset.category = cat;
      btn.addEventListener("click", () => {
        if (this.activeCategory === cat) return;
        this.activeCategory = cat;
        this.renderCategory(cat);
        this.refreshTabs();
      });
      this.rail.appendChild(btn);
      this.tabEls.set(cat, btn);
    }
    main.appendChild(this.rail);

    this.body = el("div", "prefs__body");
    main.appendChild(this.body);

    this.sheet.appendChild(main);

    // Footer
    const actions = el("div", "prefs__actions");
    this.btnCancel = el("button", "prefs__btn");
    this.btnCancel.type = "button";
    this.btnCancel.textContent = "Cancel";
    this.btnOK = el("button", "prefs__btn prefs__btn--primary");
    this.btnOK.type = "button";
    this.btnOK.textContent = "OK";
    actions.appendChild(this.btnCancel);
    actions.appendChild(this.btnOK);
    this.sheet.appendChild(actions);

    document.body.appendChild(this.root);
    this.wireEvents();
    this.built = true;
  }

  private wireEvents(): void {
    this.outsideHandler = (e: MouseEvent) => { if (e.target === this.root) this.finish(null); };
    this.root.addEventListener("mousedown", this.outsideHandler);
    this.sheet.addEventListener("mousedown", (e) => e.stopPropagation());

    this.keyHandler = (e: KeyboardEvent) => {
      if (this.root.style.display === "none") return;
      if (e.key === "Escape") { e.preventDefault(); this.finish(null); }
      else if (e.key === "Enter" && !(e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        this.commit();
      } else if (e.key === "Tab") {
        trapFocusPrefs(e, this.sheet);
      }
    };
    window.addEventListener("keydown", this.keyHandler, true);

    this.btnOK.addEventListener("click", () => this.commit());
    this.btnCancel.addEventListener("click", () => this.finish(null));
  }

  private refreshTabs(): void {
    for (const [cat, btn] of this.tabEls.entries()) {
      btn.classList.toggle("is-active", cat === this.activeCategory);
    }
  }

  private renderCategory(cat: Category): void {
    this.body.innerHTML = "";
    const h = el("div", "prefs__category-title");
    h.textContent = cat;
    this.body.appendChild(h);

    switch (cat) {
      case "General":
        this.body.appendChild(row("Image Interpolation", mkSelect(this.prefs.imageInterpolation, [
          ["nearest", "Nearest Neighbor"],
          ["bilinear", "Bilinear"],
          ["bicubic", "Bicubic (default)"],
          ["bicubic-sharper", "Bicubic Sharper"],
          ["bicubic-smoother", "Bicubic Smoother"],
        ], (v) => { this.prefs.imageInterpolation = v; })));
        this.body.appendChild(row("Resize Image During Paste", mkCheckbox(this.prefs.resizeOnPaste, (v) => { this.prefs.resizeOnPaste = v; })));
        this.body.appendChild(row("Animated Zoom", mkCheckbox(this.prefs.animatedZoom, (v) => { this.prefs.animatedZoom = v; })));
        this.body.appendChild(placeholderNote("HUD Color Picker, Flick Panning — coming soon."));
        break;

      case "Interface":
        this.body.appendChild(row("Color Theme", mkSelect<ThemeChoice>(this.prefs.theme, [
          ["dark", "Dark"], ["light", "Light"], ["auto", "Auto (follow system)"],
        ], (v) => { this.prefs.theme = v; applyTheme(v); })));
        this.body.appendChild(row("UI Font Size", mkSelect(this.prefs.uiFontSize, [
          ["small", "Small"], ["medium", "Medium"], ["large", "Large"],
        ], (v) => { this.prefs.uiFontSize = v; })));
        this.body.appendChild(row("UI Language", mkSelect(this.prefs.uiLanguage, [
          ["en-US", "English (US)"], ["en-GB", "English (UK)"], ["ja", "日本語"], ["de", "Deutsch"], ["fr", "Français"], ["zh-CN", "中文"],
        ], (v) => { this.prefs.uiLanguage = v; })));
        break;

      case "Workspace":
        this.body.appendChild(row("Open Documents As Tabs", mkCheckbox(this.prefs.openDocsAsTabs, (v) => { this.prefs.openDocsAsTabs = v; })));
        this.body.appendChild(row("Enable Floating Document Windows", mkCheckbox(this.prefs.enableFloatingDocs, (v) => { this.prefs.enableFloatingDocs = v; })));
        this.body.appendChild(placeholderNote("Auto-show panels — coming soon."));
        break;

      case "Tools":
        this.body.appendChild(row("Show Tooltips", mkCheckbox(this.prefs.showTooltips, (v) => { this.prefs.showTooltips = v; })));
        this.body.appendChild(row("Zoom With Scroll Wheel", mkCheckbox(this.prefs.zoomWithScrollWheel, (v) => { this.prefs.zoomWithScrollWheel = v; })));
        this.body.appendChild(row("Use Shift Key for Tool Switch", mkCheckbox(this.prefs.shiftKeyForToolSwitch, (v) => { this.prefs.shiftKeyForToolSwitch = v; })));
        break;

      case "History Log":
        this.body.appendChild(row("History States", mkNumber(this.prefs.historyStates, 1, 200, 1, (v) => { this.prefs.historyStates = Math.round(v); })));
        this.body.appendChild(row("Enable History Log", mkCheckbox(this.prefs.historyLogEnabled, (v) => { this.prefs.historyLogEnabled = v; })));
        this.body.appendChild(placeholderNote("Save Log Items — coming soon."));
        break;

      case "File Handling":
        this.body.appendChild(row("Image Previews", mkSelect(this.prefs.imagePreviews, [
          ["always", "Always Save"], ["ask", "Ask When Saving"], ["never", "Never Save"],
        ], (v) => { this.prefs.imagePreviews = v; })));
        this.body.appendChild(row("Append PSD Compatibility", mkSelect(this.prefs.appendPsdCompat, [
          ["always", "Always"], ["ask", "Ask"], ["never", "Never"],
        ], (v) => { this.prefs.appendPsdCompat = v; })));
        this.body.appendChild(placeholderNote("Camera Raw preferences — coming soon."));
        break;

      case "Export":
        this.body.appendChild(row("Quick Export Format", mkSelect(this.prefs.quickExportFormat, [
          ["png", "PNG"], ["jpeg", "JPEG"], ["webp", "WebP"],
        ], (v) => { this.prefs.quickExportFormat = v; })));
        this.body.appendChild(row("Quick Export Location", mkSelect(this.prefs.quickExportLocation, [
          ["ask", "Ask each time"], ["desktop", "Desktop"], ["documents", "Documents"],
        ], (v) => { this.prefs.quickExportLocation = v; })));
        break;

      case "Performance":
        this.body.appendChild(row("Memory Usage (%)", mkNumber(this.prefs.memoryUsagePercent, 10, 95, 1, (v) => { this.prefs.memoryUsagePercent = Math.round(v); })));
        this.body.appendChild(row("Cache Levels", mkNumber(this.prefs.cacheLevels, 1, 8, 1, (v) => { this.prefs.cacheLevels = Math.round(v); })));
        this.body.appendChild(row("Cache Tile Size", mkSelect<"128" | "256" | "512" | "1024">(String(this.prefs.cacheTileSize) as "128" | "256" | "512" | "1024", [
          ["128", "128 px"], ["256", "256 px"], ["512", "512 px"], ["1024", "1024 px"],
        ], (v) => { this.prefs.cacheTileSize = parseInt(v, 10) as 128 | 256 | 512 | 1024; })));
        this.body.appendChild(placeholderNote("GPU Settings, History States in Performance tab — coming soon."));
        break;

      case "Scratch Disks":
        this.body.appendChild(placeholderNote(this.prefs.scratchDiskNote));
        break;

      case "Cursors":
        this.body.appendChild(row("Painting Cursors", mkSelect(this.prefs.paintingCursor, [
          ["standard", "Standard"], ["precise", "Precise"],
          ["normal-brush-tip", "Normal Brush Tip"], ["full-size-brush-tip", "Full Size Brush Tip"],
        ], (v) => { this.prefs.paintingCursor = v; })));
        this.body.appendChild(row("Other Cursors", mkSelect(this.prefs.otherCursor, [
          ["standard", "Standard"], ["precise", "Precise"],
        ], (v) => { this.prefs.otherCursor = v; })));
        this.body.appendChild(row("Cursor Style", mkSelect<CursorChoice>(this.prefs.cursorStyle, [
          ["precise", "Precise"], ["normal", "Normal"], ["both", "Both"],
        ], (v) => { this.prefs.cursorStyle = v; })));
        break;

      case "Transparency & Gamut":
        this.body.appendChild(row("Grid Size (px)", mkNumber(this.prefs.gridSize, 4, 32, 1, (v) => { this.prefs.gridSize = Math.round(v); })));
        this.body.appendChild(row("Grid Color 1", mkColor(this.prefs.gridColor1, (v) => { this.prefs.gridColor1 = v; })));
        this.body.appendChild(row("Grid Color 2", mkColor(this.prefs.gridColor2, (v) => { this.prefs.gridColor2 = v; })));
        this.body.appendChild(row("Gamut Warning Color", mkColor(this.prefs.gamutWarningColor, (v) => { this.prefs.gamutWarningColor = v; })));
        break;

      case "Units & Rulers":
        this.body.appendChild(row("Ruler Unit", mkSelect<RulerUnit>(this.prefs.rulerUnit, [
          ["pixels", "Pixels"], ["percent", "Percent"], ["inches", "Inches"],
          ["cm", "Centimeters"], ["mm", "Millimeters"],
          ["points", "Points"], ["picas", "Picas"],
        ], (v) => { this.prefs.rulerUnit = v; })));
        this.body.appendChild(row("Type Unit", mkSelect(this.prefs.typeUnit, [
          ["points", "Points"], ["pixels", "Pixels"], ["mm", "Millimeters"],
        ], (v) => { this.prefs.typeUnit = v; })));
        break;

      case "Guides, Grid & Slices":
        this.body.appendChild(row("Guide Color", mkColor(this.prefs.guideColor, (v) => { this.prefs.guideColor = v; })));
        this.body.appendChild(row("Grid Color", mkColor(this.prefs.gridColor, (v) => { this.prefs.gridColor = v; })));
        this.body.appendChild(row("Grid Subdivisions", mkNumber(this.prefs.gridSubdivisions, 1, 100, 1, (v) => { this.prefs.gridSubdivisions = Math.round(v); })));
        break;

      case "Plug-Ins":
        this.body.appendChild(row("Allow Extensions Internet Access", mkCheckbox(this.prefs.pluginAllowInternet, (v) => { this.prefs.pluginAllowInternet = v; })));
        this.body.appendChild(placeholderNote("Plug-in folder browsing — coming soon."));
        break;

      case "Type":
        this.body.appendChild(row("Use Smart Quotes", mkCheckbox(this.prefs.smartQuotes, (v) => { this.prefs.smartQuotes = v; })));
        this.body.appendChild(row("Show Font Names in English", mkCheckbox(this.prefs.showFontNamesEnglish, (v) => { this.prefs.showFontNamesEnglish = v; })));
        this.body.appendChild(placeholderNote("Asian text options — coming soon."));
        break;

      case "3D":
        this.body.appendChild(row("Render Quality", mkSelect(this.prefs.threeDQuality, [
          ["draft", "Draft"], ["good", "Good"], ["best", "Best"],
        ], (v) => { this.prefs.threeDQuality = v; })));
        this.body.appendChild(placeholderNote("Available VRAM, Auto-hide — coming soon."));
        break;

      case "Camera Raw":
        this.body.appendChild(row("Auto Tone on Open", mkCheckbox(this.prefs.cameraRawAutoTone, (v) => { this.prefs.cameraRawAutoTone = v; })));
        this.body.appendChild(placeholderNote("Sliders, Advanced — coming soon."));
        break;
    }
  }

  /** Snapshot of the in-flight preferences. Exposed for tests. */
  getCurrent(): Preferences {
    return { ...this.prefs };
  }

  /** Programmatically switch category. Exposed for tests. */
  selectCategory(cat: Category): void {
    if (!this.built) this.build();
    this.activeCategory = cat;
    this.renderCategory(cat);
    this.refreshTabs();
  }

  private commit(): void {
    save(this.prefs);
    applyTheme(this.prefs.theme);
    this.finish({ ...this.prefs });
  }

  private finish(value: Preferences | null): void {
    if (!this.resolveFn) return;
    const fn = this.resolveFn;
    this.resolveFn = null;
    if (this.built) this.root.style.display = "none";
    // Restore focus to opener (#47).
    if (this.openerEl && "focus" in this.openerEl) {
      (this.openerEl as HTMLElement).focus();
    }
    this.openerEl = null;
    fn(value);
  }
}

// ── Local focus-trap helper ────────────────────────────────────────────────
const FOCUSABLE_SEL = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function trapFocusPrefs(e: KeyboardEvent, container: HTMLElement): void {
  if (e.key !== "Tab") return;
  const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SEL));
  if (focusable.length === 0) return;
  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;
  if (e.shiftKey) {
    if (document.activeElement === first) { e.preventDefault(); last.focus(); }
  } else {
    if (document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
}
